import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow,
  Typography, Box, Select, MenuItem, InputLabel,
  FormControl, IconButton, Grid,
  TextField,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useFetch } from '../../../hooks/useFetch';
import { CustomDateTimePicker } from '../../common/CustomDateTimePicker/CustomDateTimePicker';

const columns = [
  { id: 'timestamp_log', label: 'Fecha', minWidth: 150 },
  { id: 'app_name', label: 'Aplicación', minWidth: 100 },
  { id: 'pid_instance', label: 'PID', minWidth: 80 },
  { id: 'type_log', label: 'Tipo', minWidth: 80 },
  { id: 'message_log', label: 'Mensaje', minWidth: 200 },
];

const fields = 'app_name,timestamp_log,pid_instance,type_log,message_log';

const getFormattedDateTime = (date) => {
  return new Date(date).toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
};

const now = new Date();
const yesterday = new Date(now);
yesterday.setDate(now.getDate() - 1);

export const LogScreenHistory = () => {


  const [selectedApp, setSelectedApp] = useState('Todas');
  const [logType, setLogType] = useState('Todos');
  const [searchText, setSearchText] = useState('');
  const [from, setFrom] = useState(getFormattedDateTime(yesterday));
  const [to, setTo] = useState(getFormattedDateTime(now));
  const [searchUrl, setSearchUrl] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [isSearching, setIsSearching] = useState(false);

  const { data: appList, loading: loadingApps, error: errorApps } = useFetch(`logs/apps`);
  const { data: logs, loading: loadingLogs, error: errorLogs } = useFetch(searchUrl);

  const buildSearchUrl = (targetPage) => {
    const offset = targetPage * rowsPerPage;
    const limit = rowsPerPage;

    let url = `logs?fields=${fields}&limit=${limit}&offset=${offset}&count=true`;
    if (selectedApp && selectedApp !== 'Todas') {
      url += `&app_name=${selectedApp}`;
    }
    if (logType && logType !== 'Todos') {
      url += `&type_log=${logType}`;
    }
    if (searchText.trim()) {
      url += `&search=${encodeURIComponent(searchText.trim())}`; //Busqueda escrita
    }
    if (from) {
      url += `&from=${encodeURIComponent(from)}`;
    }
    if (to) {
      url += `&to=${encodeURIComponent(to)}`;
    }
    return url;
  };


  const handleSearch = () => {
    setIsSearching(true);
    setPage(0);
    setSearchUrl(buildSearchUrl(0));
  };

  useEffect(() => {
    if (!loadingLogs) {
      setIsSearching(false);
    }
  }, [loadingLogs]);

  const visibleRows = useMemo(() => {
    if (!logs || !Array.isArray(logs)) return [];

    const rows = logs[0]?.data ?? [];

    if (!searchText.trim()) return rows;

    const lowerSearch = searchText.toLowerCase();

    return rows.filter((row) =>
      Object.values(row).some(
        (val) =>
          typeof val === 'string' &&
          val.toLowerCase().includes(lowerSearch)
      )
    );
  }, [logs, searchText]);


  useEffect(() => {
    if (logs?.[0]?.count !== undefined) {
      setTotalCount(logs[0].count);
    }
  }, [logs]);


  useEffect(() => {
    if (searchUrl) {
      setIsSearching(true);
      setSearchUrl(buildSearchUrl(page));
    }
  }, [page, rowsPerPage]);


  return (
    <Box sx={{ padding: 2 }}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth>
            <InputLabel>Aplicación</InputLabel>
            <Select
              value={selectedApp}
              label="Aplicación"
              onChange={(e) => setSelectedApp(e.target.value)}
            >
              <MenuItem value="Todas">Todas</MenuItem>
              {loadingApps && <MenuItem disabled>Cargando...</MenuItem>}
              {errorApps && <MenuItem disabled>Error al cargar</MenuItem>}
              {appList?.map((app) => (
                <MenuItem key={app} value={app}>{app}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth>
            <InputLabel>Tipo</InputLabel>
            <Select
              value={logType}
              label="Tipo"
              onChange={(e) => setLogType(e.target.value)}
            >
              <MenuItem value="Todos">Todos</MenuItem>
              <MenuItem value="info">info</MenuItem>
              <MenuItem value="error">error</MenuItem>
              <MenuItem value="debug">debug</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <CustomDateTimePicker
            label="Desde"
            value={from}
            onChange={setFrom}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <CustomDateTimePicker
            label="Hasta"
            value={to}
            onChange={setTo}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            label="Buscar"
            variant="outlined"
            fullWidth
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
          />
        </Grid>


        <Grid item xs={12} sm={6} md="auto">
          <IconButton onClick={handleSearch} sx={{ mt: { xs: 1, sm: 0 } }}>
            <SearchIcon />
          </IconButton>
        </Grid>
      </Grid>



      {errorLogs && (
        <Typography color="error" sx={{ padding: 2 }}>
          Error al cargar los logs.
        </Typography>
      )}
      {isSearching && (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {!isSearching && logs && (
        <Paper elevation={0}
          sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small" aria-label="tabla de logs históricos">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align="left"
                      style={{ minWidth: column.minWidth }}

                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row, rowIndex) => (
                  <TableRow hover key={rowIndex}>
                    {columns.map((column) => {
                      let value = row[column.id];
                      if (column.id === 'timestamp_log') {
                        value = new Date(value).toLocaleString();
                      }
                      return (
                        <TableCell key={column.id} align="left">
                          {value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            labelRowsPerPage="Logs por página"
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(+e.target.value);
              setPage(0);
            }}
          />
        </Paper>
      )}
    </Box>
  );
};
